// valueSlotCapabilities — value-slot plan T5b (D4). The per-engine capability
// table that governs `deriveCompletionContext`'s THIRD slot-seam gate: a
// grammar-recognised `scalar-value` slot classifies only when the engine's
// capability set contains the slot's role AND (the clause is in the role's
// override set OR the clause machine yielded `'unknown'`) — the capability
// check governs BOTH paths.
//
// T5b shipped the table EMPTY for every engine (the gate dormant — zero
// production classification changes; `slotNoWorse.test.ts` pins it). Each
// per-engine flip task adds its role(s) to the engine's set once the engine's
// resolver branch + policy wiring have landed. T13a flipped pgsql for
// `'comparison-rhs'`; T13b flipped mssql; T13c flipped mysql; T13d flipped
// oracle; T13e flipped sqlite — the T13 wave is now COMPLETE: every engine
// carries `'comparison-rhs'` in production. Tests exercise the gate ahead of
// a flip through the P2-6 seam (`__setValueSlotCapabilitiesForTest`),
// clearing it in `afterEach`.

import type { DatabaseEngineId } from '@/core/engines';
import type { SqlClause, ValueSlotRole } from '@/features/sql-grammar/model';

const EMPTY_ROLES: ReadonlySet<ValueSlotRole> = new Set<ValueSlotRole>();

/** Production capability table. pgsql went live for `'comparison-rhs'` at
 *  T13a, mssql at T13b, mysql at T13c, oracle at T13d, sqlite at T13e — the
 *  T13 wave is complete. T15 added `'set-rhs'` (DML SET assignment RHS) for
 *  all five engines in one slice — the recognizer itself refuses non-DML SET
 *  statements, so the role is uniform. T16 added `'between-low'` /
 *  `'between-high'` (BETWEEN bounds, incl. NOT BETWEEN) for all five engines
 *  in one slice too — the recognizer's window-frame guard keeps
 *  `ROWS|RANGE|GROUPS BETWEEN ▮` out, so the role is uniform here as well.
 *  Later roles (in-list-item — T17) will extend these sets as their slice
 *  lands. */
export const VALUE_SLOT_CAPABILITIES: ReadonlyMap<DatabaseEngineId, ReadonlySet<ValueSlotRole>> =
  new Map<DatabaseEngineId, ReadonlySet<ValueSlotRole>>([
    ['mssql', new Set<ValueSlotRole>(['comparison-rhs', 'set-rhs', 'between-low', 'between-high', 'in-list-item'])],
    ['pgsql', new Set<ValueSlotRole>(['comparison-rhs', 'set-rhs', 'between-low', 'between-high', 'in-list-item'])],
    ['mysql', new Set<ValueSlotRole>(['comparison-rhs', 'set-rhs', 'between-low', 'between-high', 'in-list-item'])],
    ['oracle', new Set<ValueSlotRole>(['comparison-rhs', 'set-rhs', 'between-low', 'between-high', 'in-list-item'])],
    ['sqlite', new Set<ValueSlotRole>(['comparison-rhs', 'set-rhs', 'between-low', 'between-high', 'in-list-item'])],
  ]);

/** Clauses where a role's slot result may override a confidently-named clause
 *  classification (D4). The clause machine names every SET flavor
 *  `'update-set'` (G12) — safe as `set-rhs`'s override because the recognizer
 *  already refused the non-DML flavors. BETWEEN bounds override the SAME
 *  clause set as comparison-rhs (where/having/join-on) — a BETWEEN predicate
 *  lives in exactly those clauses. Roles added by a later stage
 *  (in-list-item — T17) bring their own set. */
export const VALUE_SLOT_OVERRIDE_CLAUSES: ReadonlyMap<ValueSlotRole, ReadonlySet<SqlClause>> =
  new Map<ValueSlotRole, ReadonlySet<SqlClause>>([
    ['comparison-rhs', new Set<SqlClause>(['where', 'having', 'join-on'])],
    ['set-rhs', new Set<SqlClause>(['update-set'])],
    ['between-low', new Set<SqlClause>(['where', 'having', 'join-on'])],
    ['between-high', new Set<SqlClause>(['where', 'having', 'join-on'])],
    // T17: `IN (` opens a `'subquery'` clause frame (G12) — that frame IS the
    // in-list-item position, so this role's override set is exactly it. The
    // recognizer refuses subquery interiors (SELECT present) itself.
    ['in-list-item', new Set<SqlClause>(['subquery'])],
  ]);

let capabilitiesTestOverride:
  | ReadonlyMap<DatabaseEngineId, ReadonlySet<ValueSlotRole>>
  | null = null;

/** P2-6 test seam: replace the production table for the duration of a test.
 *  Pass `null` to restore it — callers MUST clear in `afterEach`. */
export function __setValueSlotCapabilitiesForTest(
  map: ReadonlyMap<DatabaseEngineId, ReadonlySet<ValueSlotRole>> | null,
): void {
  capabilitiesTestOverride = map;
}

/** The active role set for `engineId` — the test override when installed,
 *  the production table otherwise. Missing engines resolve to the empty set. */
export function valueSlotRolesForEngine(engineId: DatabaseEngineId): ReadonlySet<ValueSlotRole> {
  return (capabilitiesTestOverride ?? VALUE_SLOT_CAPABILITIES).get(engineId) ?? EMPTY_ROLES;
}
